import { syncRule, childId, validity, descriptionSrcURL } from '../common/decoration';
import type {
    TableColumnVerticalAlignment,
    StatePreservationMode,
    DraftDiscardConfirmationSettings,
    Target,
    Application,
    FlexibleColumnLayout,
    Manifest,
    CloudDevAdaptationStatus
} from '@sap/ux-specification-types';
import { v2, ManifestSection, GENERICAPPSETTINGS, VOCWITHCOLONS } from '@sap/ux-specification-types';

export const getAppSettingPathV2 = (pageKeys: string[]): string => {
    return `${ManifestSection.generic}/pages/${pageKeys[0]}/component/settings`;
};

export const getTableSettingPathV2 = (pageKeys: string[]): string => {
    return getAppSettingPathV2(pageKeys) + '/tableSettings';
};

export const getChartSettingPathV2 = (pageKeys: string[]): string => {
    return getAppSettingPathV2(pageKeys) + '/chartSettings';
};

export const getDataLoadSettingPathV2 = (pageKeys: string[]): string => {
    return getAppSettingPathV2(pageKeys) + '/dataLoadSettings';
};

export const getCreationFieldsPathV2 = (pageKeys: string[], key: string): string => {
    return `${getTableSettingPathV2(pageKeys)}/createWithParameterDialog/fields/${key}`;
};

export const getOVPsettingPath = (): string => {
    return ManifestSection.ovp;
};

export const getCardSettingsPath = (cardKeys: string[]): string => {
    const pathParts: string[] = [ManifestSection.ovp];
    cardKeys.forEach((block) => {
        if (block in v2.CardSettingsType) {
            pathParts.push('settings');
        } else {
            pathParts.push(block);
        }
    });
    pathParts.pop();
    return pathParts.join('/');
};

export const getGlobalSettingsPath = (cardKey: string[]): string => {
    const pathParts: string[] = [ManifestSection.ovp];
    cardKey.forEach((block) => {
        pathParts.push(block);
    });
    pathParts.pop();
    return pathParts.join('/');
};

// This corresponds to ALP KPI settings
export const getKPISettingsPath = (pageKeys: string[], kpiKey: string): string => {
    return `${ManifestSection.generic}/pages/${pageKeys[0]}/component/settings/keyPerformanceIndicators/${kpiKey}`;
};

// This corresponds to List Report and Object page global settings
export const getAppRootSettingOBJPath = (pageKeys: string[]): string => {
    const pathParts: string[] = [ManifestSection.generic];
    pageKeys.forEach((pageKey) => {
        pathParts.push('pages/' + pageKey);
    });
    return pathParts.join('/');
};

// This corresponds to global object page settings
export const getAppSettingOBJPathV2 = (pageKeys: string[]): string => {
    const pathParts: string[] = [];
    pathParts.push(getAppRootSettingOBJPath(pageKeys));
    pathParts.push('component/settings');
    return pathParts.join('/');
};

export const getGlobalTableSettingsPathV2 = (pageKeys: string[]): string => {
    const path = getAppSettingOBJPathV2(pageKeys);
    return path + '/tableSettings';
};

export const getAppSettingOBJsectionPathV2 = (pageKeys: string[], _manifest: Manifest, sectionId: string): string => {
    const pathParts: string[] = [];
    pathParts.push(getAppRootSettingOBJPath(pageKeys));
    const manifestSectionId = sectionId.replace(VOCWITHCOLONS, '::com.sap.vocabularies');
    pathParts.push('component/settings/sections/' + manifestSectionId);
    return pathParts.join('/');
};

export const getTableSettingsOBJsectionPathV2 = (
    pageKeys: string[],
    _manifest: Manifest,
    sectionId: string
): string => {
    const path = getAppSettingOBJsectionPathV2(pageKeys, {} as Manifest, sectionId);
    return path + '/tableSettings';
};

export const getChartSettingsOBJsectionPathV2 = (
    pageKeys: string[],
    _manifest: Manifest,
    sectionId: string
): string => {
    const path = getAppSettingOBJsectionPathV2(pageKeys, {} as Manifest, sectionId);
    return path + '/chartSettings';
};

export const getViewExtensionsPath = (): string => {
    return `sap.ui5/extends/extensions/${v2.SAPUI5_VIEW_EXTENSION}/${v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE}`;
};

export const getObjectPageCustomSectionPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    ...pathParts: string[]
): string => {
    const separator = '|';
    const sectionIdParts = sectionId.split(separator);
    const pageNameParts = pageName[pageName.length - 1].split(separator);
    let idLastPart = '';
    if (sectionIdParts.length === 4) {
        // Four parts ==> the last one is incremented to avoid getting the same path
        // We need to merge 3 and 4
        idLastPart = [sectionIdParts[2], sectionIdParts[3]].join(separator);
    } else if (sectionIdParts.length === 3) {
        idLastPart = sectionIdParts[2];
    }
    const customSectionId = [sectionIdParts[0], pageNameParts[pageNameParts.length - 1], idLastPart].join(separator);
    // Sub-path parts are passed
    pathParts.unshift(customSectionId);

    return `${getViewExtensionsPath()}/${pathParts.join('/')}`;
};

export class ApplicationV2 implements Application {
    id?: string;
    pages?: v2.Pages;
    home?: string;
    target?: Target;
    @childId('settings')
    settings?: AppSettings;
}

export class AppSettings implements v2.AppSettings {
    @syncRule({
        manifest: {
            path: (): string => 'sap.app'
        }
    })
    title?: string;

    @syncRule({
        manifest: {
            path: (): string => 'sap.app'
        }
    })
    description?: string;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fiori'
        }
    })
    cloudDevAdaptationStatus?: CloudDevAdaptationStatus;

    @syncRule({
        manifest: {
            path: (): string => GENERICAPPSETTINGS
        }
    })
    @validity({
        since: '1.50.0'
    })
    flexibleColumnLayout?: FlexibleColumnLayout;

    @syncRule({
        manifest: {
            path: (): string => GENERICAPPSETTINGS
        }
    })
    @validity({
        since: '1.46.0'
    })
    forceGlobalRefresh?: boolean;

    @syncRule({
        manifest: {
            path: (): string => GENERICAPPSETTINGS
        }
    })
    @validity({
        since: '1.74.0'
    })
    tableColumnVerticalAlignment?: TableColumnVerticalAlignment;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5'
        }
    })
    @validity({
        since: '1.56.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/ccd45ba3f0b446a0901b2c9d42b8ad53')
    flexEnabled?: boolean;

    @syncRule({
        manifest: {
            path: (): string => GENERICAPPSETTINGS
        }
    })
    @validity({
        since: '1.96.0'
    })
    draftDiscardConfirmationSettings?: DraftDiscardConfirmationSettings;

    @syncRule({
        manifest: {
            path: (): string => GENERICAPPSETTINGS
        }
    })
    @validity({
        since: '1.96.0'
    })
    statePreservationMode?: StatePreservationMode;

    @syncRule({
        manifest: {
            path: (): string => {
                return GENERICAPPSETTINGS;
            },
            import: (value: object): object | undefined => {
                if ('tableSettings' in value) {
                    return value['tableSettings'] as object | undefined;
                }
            },
            key: 'tableSettings'
        }
    })
    @validity({
        since: '1.48.5'
    })
    globalTableSettings?: v2.GlobalApplicationTableSettings;
}
